/*
  Utils to parse property parameters for an association.
*/

import { clone, map, uniq, assoc } from 'ramda';

import { getAssociatedAttribute } from './avenyModelUtils';

import {
  IClientAssociationModel,
  IClientAttributeModel,
} from '../Types/ClientModelTypes';
import { AvenyCollectionMap } from './ModelContext/ModelContext';
import Logger, { namedError } from '../Utils/Logger';

type IntegratedFilterType = string[];
interface IDisplayLabelsOverrides {
  [key: string]: string;
}
interface IOverrideDisplayLabels {
  groupName?: string;
  prefix?: string;
  overrides: IDisplayLabelsOverrides;
}
export interface IAssociationProperties {
  association: IClientAssociationModel;
  integratedFilter: IntegratedFilterType | null;
  overrideDisplayLabels: IOverrideDisplayLabels | null;
}

export const getIntegratedFilter = (
  propertyMap: Map<string, string>
): IntegratedFilterType | null => {
  const integratedFilterValueString: string | undefined =
    propertyMap.get('IntegratedFilter');
  return integratedFilterValueString
    ? uniq(integratedFilterValueString.replace(';', '').split(','))
    : null;
};

const parseOverrideDisplayLabelStrings = (
  overrideDisplayLabelStrings: string[]
) => {
  const overrideDisplayLabels: IOverrideDisplayLabels = {
    groupName: undefined,
    prefix: undefined,
    overrides: {},
  };

  for (let overrideDisplayLabelString of overrideDisplayLabelStrings) {
    if (overrideDisplayLabelString.includes(':*=')) {
      // Is special overrideDisplayLabels value
      if (overrideDisplayLabelString.startsWith('G:')) {
        overrideDisplayLabels.groupName = overrideDisplayLabelString
          .replace(';', '')
          .split('*=')[1]
          .slice(1, -1);
      } else if (overrideDisplayLabelString.startsWith('PREFIX:')) {
        overrideDisplayLabels.prefix = overrideDisplayLabelString
          .replace(';', '')
          .split('*=')[1]
          .slice(1, -1);
      } else {
        Logger.warn(
          namedError(
            'AssociationPropertiesUtils-NoHandlingImplemented',
            `No handling for overrideDisplayLabel: ${overrideDisplayLabelString} implemented`
          )
        );
      }
    } else {
      // Is normal overrideDisplayLabels value
      const keyValuePair = overrideDisplayLabelString.split('=');
      if (keyValuePair[0] != null && keyValuePair[1] != null) {
        overrideDisplayLabels.overrides[keyValuePair[0]] = keyValuePair[1]
          .replace(';', '')
          .slice(1, -1);
      }
    }
  }

  return overrideDisplayLabels;
};

const getOverrideDisplayLabels = (
  propertyMap: Map<string, string>
): IOverrideDisplayLabels | null => {
  const overrideDisplayLabelsValueString: string | undefined = propertyMap.get(
    'OverrideDisplayLabels'
  );

  return overrideDisplayLabelsValueString
    ? parseOverrideDisplayLabelStrings(
        // Split the string by commas, but ignore commas within double-quotes
        overrideDisplayLabelsValueString.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/gm)
      )
    : null;
};

const getAssociationProperties = (
  association: IClientAssociationModel
): IAssociationProperties => {
  return {
    association,
    integratedFilter: getIntegratedFilter(association.Properties),
    overrideDisplayLabels: getOverrideDisplayLabels(association.Properties),
  };
};

const filterIntegrated = (
  integratedFilter: IntegratedFilterType,
  attributes: IClientAttributeModel[],
  association: IClientAssociationModel,
  collectionMap: AvenyCollectionMap
): IClientAttributeModel[] => {
  let newAttributes = [];
  for (let integratedName of integratedFilter) {
    for (let j = 0; j < attributes.length; j++) {
      if (attributes[j].AttributeName === integratedName) {
        newAttributes.push(attributes[j]);
        break;
      } else if (j === attributes.length - 1) {
        // Tried to filter on an attribute that does not exist in attributes
        const filterPath = integratedName.split('.');

        // If we filter on an associated attribute, force add the attribute
        if (filterPath.length > 1) {
          let forcedAttribute = getAssociatedAttribute(
            association.AssociatedCollectionName,
            filterPath,
            collectionMap
          );

          if (forcedAttribute != null) {
            forcedAttribute = assoc(
              'AttributeName',
              filterPath.join('.'),
              forcedAttribute
            );

            newAttributes.push(forcedAttribute);
          } else {
            Logger.error(
              `Unsuccessfully tried to get attribute ${
                filterPath[filterPath.length - 1]
              }`
            );
          }
        }
      }
    }
  }

  return newAttributes;
};

const filteredAttributes = (
  integratedFilter: IntegratedFilterType | null,
  attributes: IClientAttributeModel[],
  association: IClientAssociationModel,
  collectionMap: AvenyCollectionMap
): IClientAttributeModel[] => {
  if (integratedFilter != null) {
    return filterIntegrated(
      integratedFilter,
      attributes,
      association,
      collectionMap
    );
  } else {
    return attributes;
  }
};

const filteredAssociations = (
  integratedFilter: IntegratedFilterType | null,
  associations: IClientAssociationModel[],
  association: IClientAssociationModel
): IClientAssociationModel[] => {
  if (integratedFilter != null) {
    return [];
  } else {
    return associations;
  }
};

const overrideAttrOrAssocDisplayLabel =
  (name: string) =>
  <T extends IClientAttributeModel | IClientAssociationModel>(
    overrideDisplayLabels: IOverrideDisplayLabels,
    attrOrAssoc: T
  ): void => {
    if (
      overrideDisplayLabels.overrides &&
      overrideDisplayLabels.overrides[(attrOrAssoc as unknown as Record<string, string>)[name]]
    ) {
      attrOrAssoc.DisplayLabel =
        overrideDisplayLabels.overrides[(attrOrAssoc as unknown as Record<string, string>)[name]];
    }

    if (overrideDisplayLabels.prefix != null) {
      attrOrAssoc.DisplayLabel = `${overrideDisplayLabels.prefix}${attrOrAssoc.DisplayLabel}`;
    }
  };

const overrideAttributeDisplayLabel: (
  overrideDisplayLabels: IOverrideDisplayLabels,
  attrOrAssoc: IClientAttributeModel
) => void = overrideAttrOrAssocDisplayLabel('AttributeName');

const overrideAssociationDisplayLabel: (
  overrideDisplayLabels: IOverrideDisplayLabels,
  attrOrAssoc: IClientAssociationModel
) => void = overrideAttrOrAssocDisplayLabel('AssociationName');

const overrideDisplayLabelsAttr =
  (overrideDisplayLabels: IOverrideDisplayLabels) =>
  (attribute: IClientAttributeModel): IClientAttributeModel => {
    const newAttribute = clone(attribute);

    overrideAttributeDisplayLabel(overrideDisplayLabels, newAttribute);

    if (overrideDisplayLabels.groupName != null) {
      newAttribute.AttributeGroup = overrideDisplayLabels.groupName;
    }

    return newAttribute;
  };

const overrideDisplayLabelsAssoc =
  (overrideDisplayLabels: IOverrideDisplayLabels) =>
  (association: IClientAssociationModel): IClientAssociationModel => {
    const newAttribute = clone(association);

    overrideAssociationDisplayLabel(overrideDisplayLabels, association);

    return newAttribute;
  };

const applyPropertiesAttr = (
  attributes: IClientAttributeModel[],
  properties: IAssociationProperties,
  collectionMap: AvenyCollectionMap
): IClientAttributeModel[] => {
  let newAttributes = filteredAttributes(
    properties.integratedFilter,
    attributes,
    properties.association,
    collectionMap
  );

  if (properties.overrideDisplayLabels != null) {
    const { overrideDisplayLabels } = properties;
    newAttributes = map(
      overrideDisplayLabelsAttr(overrideDisplayLabels),
      newAttributes
    );
  }

  return newAttributes;
};

const applyPropertiesAssoc = (
  associations: IClientAssociationModel[],
  properties: IAssociationProperties
): IClientAssociationModel[] => {
  let newAssociations = filteredAssociations(
    properties.integratedFilter,
    associations,
    properties.association
  );

  if (properties.overrideDisplayLabels != null) {
    const { overrideDisplayLabels } = properties;
    newAssociations = map(
      overrideDisplayLabelsAssoc(overrideDisplayLabels),
      newAssociations
    );
  }

  return newAssociations;
};

/**
 * Creates a new list of attributes based on the properties of an association
 *
 * @param attributes List of attributes that should be affected by the properties
 * @param association The association that the properties that will be used are on
 */
export const applyAssociationPropertiesAttr = (
  attributes: IClientAttributeModel[],
  association: IClientAssociationModel,
  collectionMap: AvenyCollectionMap
): IClientAttributeModel[] => {
  return applyPropertiesAttr(
    attributes,
    getAssociationProperties(association),
    collectionMap
  );
};

/**
 * Creates a new list of associations based on the properties of an association
 *
 * @param associations List of associations that should be affected by the properties
 * @param association The association that the properties that will be used are on
 */
export const applyAssociationPropertiesAssoc = (
  associations: IClientAssociationModel[],
  association: IClientAssociationModel
): IClientAssociationModel[] => {
  return applyPropertiesAssoc(
    associations,
    getAssociationProperties(association)
  );
};
